"use client";

import {
  Box,
  Button,
  CallToAction,
  Dialog,
  Grid2,
  Popover,
  Typography,
} from "@repo/ui";
import Section from "../components/Section";
import Image, { getImageProps } from "next/image";
import moment from "moment";
import { getBackgroundImage, hasData } from "@/lib/helpers";
import BreadcrumbsLink from "@/components/common/BreadcrumbsLink";
import DownloadBrochureForm from "@/components/forms/DownloadBrochureForm";
import React, { useMemo, useState } from "react";
import SocialShare from "@/components/common/SocialShare";
import { ShareIcon } from "@spoctech/icons";

type AccreditationsType = {
  imagePath?: string;
  text?: string;
};

export type BannerWithBackgroundColorContentType = {
  imagePath?: string;
  imageAltText?: string;
  primaryButtonAction?: string;
  primaryButtonText?: string;
  secondaryButtonAction?: string;
  secondaryButtonText?: string;
  heading: string;
  paragraph?: string;
  color?: "orange" | "blue" | "rose";
  showCreatedDate: boolean;
  breadcrumbs: BreadcrumbsType[];
  logos?: AccreditationsType[];
  backgroundBannerImage?: string;
};

type BannerWithBackgroundColorProps = {
  sectionInfo: PageSections;
  data: BannerWithBackgroundColorContentType;
  createdAt?: string;
  updatedAt?: string;
};

const BannerWithBackgroundColor = (props: BannerWithBackgroundColorProps) => {
  const { data, updatedAt, sectionInfo } = props;
  const [open, setOpen] = useState(false);

  const hasPrimaryPopup = useMemo(() => {
    return (
      data.primaryButtonAction && data.primaryButtonAction?.endsWith("_POPUP")
    );
  }, [data]);

  const hasSecondaryPopup = useMemo(() => {
    return (
      data.secondaryButtonAction &&
      data.secondaryButtonAction?.endsWith("_POPUP")
    );
  }, [data]);

  if (!hasData(data)) return null;

  const {
    imageAltText = "image",
    imagePath,
    color = "orange",
    heading,
    paragraph,
    breadcrumbs,
    logos,
    primaryButtonAction,
    primaryButtonText,
    secondaryButtonAction,
    secondaryButtonText,
    showCreatedDate,
    backgroundBannerImage,
  } = data;

  const bgColorMapping = {
    orange: "bg-orange-50",
    blue: "bg-blue-50",
    rose: "bg-rose-50",
  };

  const {
    props: { srcSet },
  } = getImageProps({
    alt: "",
    width: 1500,
    height: 555,
    priority: true,
    quality: 100,
    src: typeof backgroundBannerImage === "string" ? backgroundBannerImage : "",
  });
  const backgroundImage = getBackgroundImage(srcSet);
  const style: React.CSSProperties = {
    backgroundImage,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    // backgroundPosition: "right",
  };

  return (
    <Section
      id={sectionInfo.sectionId}
      className={`${sectionInfo.sectionClass} ${bgColorMapping[color]} lg:bg-right md:bg-center bg-left`}
      style={style}
    >
      <BreadcrumbsLink breadcrumbs={breadcrumbs} />

      <Grid2 container spacing={4} align={"center"} className="min-h-[350px]">
        <Grid2 size={{ sm: 12, md: 8 }} className="group">
          {heading && (
            <Typography
              variant="h1"
              gutterBottom
              className="!leading-snug tracking-wide !font-semi-bold"
            >
              {heading}
            </Typography>
          )}
          {paragraph && (
            <Typography paragraph className="text-gray-700 tracking-wide">
              {paragraph}
            </Typography>
          )}
          {showCreatedDate && (
            <Typography paragraph className="text-gray-700 tracking-wide">
              {`Last Updated on ${moment(updatedAt).format("DD MMMM YYYY")}`}
            </Typography>
          )}

          <CallToAction
            size="large"
            className="mb-6"
            isResponsive
            onSaveLabel={
              typeof primaryButtonText === "string" ? primaryButtonText : ""
            }
            onCancelLabel={
              typeof secondaryButtonText === "string" ? secondaryButtonText : ""
            }
            onSaveHidden={!primaryButtonText || !primaryButtonAction}
            onCancelHidden={!secondaryButtonText || !secondaryButtonAction}
            onSaveLink={
              hasPrimaryPopup ? undefined : primaryButtonAction || "#"
            }
            onCancelLink={
              hasSecondaryPopup ? undefined : secondaryButtonAction || "#"
            }
            onCancel={() => {
              if (hasSecondaryPopup) {
                setOpen(true);
              }
            }}
            onSave={() => {
              if (hasPrimaryPopup) {
                setOpen(true);
              }
            }}
          />

          {logos && logos.length > 0 && (
            <Box className="my-4 sm:my-6 lg:my-8">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3 md:gap-4 items-center">
                {data?.logos?.map((item, index) => (
                  <div
                    key={`accreditation-by-logo-${index}`}
                    className="bg-common-white p-1 sm:p-1.5 h-full flex items-center justify-center rounded-lg drop-shadow-lg hover:drop-shadow-xl transition-shadow duration-300"
                  >
                    {item?.imagePath && (
                      <Image
                        src={item?.imagePath || ""}
                        alt={item?.text || ""}
                        width={90}
                        height={40}
                        priority
                        quality={100}
                        className="h-6 sm:h-8 md:h-10 lg:h-12 w-auto object-contain a-logo"
                      />
                    )}
                  </div>
                ))}
              </div>
            </Box>
          )}
        </Grid2>
        <Grid2 size={{ sm: 12, md: 4 }} className="relative hidden sm:block">
          {imagePath && !backgroundBannerImage && (
            <div className="rounded-2xl overflow-hidden">
              <Image
                src={imagePath}
                quality={100}
                alt={imageAltText}
                className="object-contain bg-blend-color object-bottom"
                width={400}
                height={500}
                priority
              />
              <Popover
                side="right"
                trigger={
                  <Button
                    color="primary"
                    aria-label="share"
                    icon={<ShareIcon />}
                    title="Share this course"
                    className="absolute bottom-4 left-4 z-10"
                  />
                }
              >
                <SocialShare />
              </Popover>
            </div>
          )}
        </Grid2>
      </Grid2>

      {/* DownloadBrochure popUp */}
      <Dialog open={open} onOpenChange={setOpen} maxWidth="md" className="!p-0">
        <DownloadBrochureForm />
      </Dialog>
    </Section>
  );
};

export default BannerWithBackgroundColor;
